import { useState, useEffect, useMemo, useRef } from "react";
import { CourseDetailsPanel } from "@/components/courses/CourseDetailsPanel";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { Navigation } from "@/components/Navigation";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Clock, Users, Star, Search, Loader2, Tag } from "lucide-react";
import { Footer } from "@/components/Footer";
import { fetchAllCoursesWithPromotions, fetchCategories } from "@/services/courseService";
import { SkeletonCourseGrid } from "@/components/ui/skeleton-course-card";
import { usePerformanceMonitor, usePageLoadPerformance } from "@/hooks/usePerformanceMonitor";
import { useOptimizedQuery } from "@/hooks/useOptimizedQuery";
import { getCourseStatus } from "@/utils/courseStatus";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate, useSearchParams } from "react-router-dom";

interface Course {
  id: string;
  slug: string;
  title: string;
  description: string;
  category: string;
  level: string;
  duration: string;
  price: number;
  instructor_name: string;
  rating: number;
  students_count: number;
  thumbnail_url: string;
  syllabus?: any;
  is_published?: boolean;
  is_coming_soon?: boolean;
  test_mode?: boolean;
  discount_percentage?: number;
  activePromotion?: {
    name: string;
    discount_percentage: number;
    end_date: string;
  };
  discounted_price?: number;
}

export default function Courses() {
  const [filteredCourses, setFilteredCourses] = useState<Course[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [levelFilter, setLevelFilter] = useState<string>("all");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [sortBy, setSortBy] = useState<string>("popular");
  const [canSeeTestCourses, setCanSeeTestCourses] = useState(false);
  const [selectedCourseId, setSelectedCourseId] = useState<string | null>(null);
  const { measureAsync } = usePerformanceMonitor("Courses");
  usePageLoadPerformance("Courses");
  const { t } = useTranslation();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // Sync ?course=<id> URL param with the details panel (deep-linkable)
  useEffect(() => {
    const courseParam = searchParams.get("course");
    if (courseParam && courseParam !== selectedCourseId) {
      setSelectedCourseId(courseParam);
    } else if (!courseParam && selectedCourseId) {
      setSelectedCourseId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const openCoursePanel = (courseId: string) => {
    setSelectedCourseId(courseId);
    const next = new URLSearchParams(searchParams);
    next.set("course", courseId);
    setSearchParams(next, { replace: false });
  };

  const closeCoursePanel = () => {
    setSelectedCourseId(null);
    const next = new URLSearchParams(searchParams);
    next.delete("course");
    setSearchParams(next, { replace: true });
  };
  const navigate = useNavigate();

  // Logged-in users get the in-dashboard catalogue (DashboardShell + sidebar).
  // Public /courses page stays as-is for SEO and shareable links.
  // Idempotent: only redirect once per mount to avoid loops with searchParams effects.
  const hasRedirected = useRef(false);
  useEffect(() => {
    if (!user || hasRedirected.current) return;
    hasRedirected.current = true;
    const courseId = new URL(window.location.href).searchParams.get("course");
    const target = `/my-courses?tab=catalogue${courseId ? `&course=${courseId}` : ""}`;
    navigate(target, { replace: true });
  }, [user, navigate]);

  // Fetch current user's enrollments so the details panel can show
  // "Continue / Review" instead of "View full page + Pay" for enrolled courses.
  const { data: myEnrollments = [] } = useOptimizedQuery({
    queryKey: ['my-enrollment-status', user?.id ?? 'anon'],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data } = await supabase
        .from('enrollments')
        .select('id, course_id, progress, completed_at, enrolled_at')
        .eq('user_id', user.id);
      return data ?? [];
    },
    dataType: 'dynamic',
    enabled: !!user?.id,
  });
  const enrollmentsMap = useMemo(() => {
    const m = new Map<string, any>();
    (myEnrollments as any[]).forEach((e) => m.set(e.course_id, e));
    return m;
  }, [myEnrollments]);

  // Detect admin/instructor role to allow seeing test_mode courses in the public catalog.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id);
      if (cancelled) return;
      const roles = (data || []).map((r: any) => r.role);
      setCanSeeTestCourses(roles.includes("admin") || roles.includes("instructor"));
    })();
    return () => { cancelled = true; };
  }, []);

  // Use optimized queries with caching
  const { data: courses = [], isLoading: coursesLoading } = useOptimizedQuery({
    queryKey: ['courses-with-promotions'],
    queryFn: fetchAllCoursesWithPromotions,
    dataType: 'static',
  });

  const { data: categories = [], isLoading: categoriesLoading } = useOptimizedQuery({
    queryKey: ['categories'],
    queryFn: fetchCategories,
    dataType: 'static',
  });

  const isLoading = coursesLoading || categoriesLoading;

  // Filter and sort courses whenever data or filters change
  useEffect(() => {
    if (courses.length > 0) {
      filterAndSortCourses();
    }
  }, [courses, searchTerm, categoryFilter, levelFilter, typeFilter, sortBy, canSeeTestCourses]);

  const filterAndSortCourses = () => {
    let filtered = [...courses];

    // Hide test_mode courses from the public catalog (admins/instructors see them).
    if (!canSeeTestCourses) {
      filtered = filtered.filter((course) => !course.test_mode);
    }

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (course) =>
          course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          course.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
          course.instructor_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Category filter
    if (categoryFilter !== "all") {
      filtered = filtered.filter((course) => course.category === categoryFilter);
    }

    // Level filter
    if (levelFilter !== "all") {
      filtered = filtered.filter((course) => course.level === levelFilter);
    }

    // Type filter (free / paid / coming soon)
    if (typeFilter !== "all") {
      filtered = filtered.filter((course) => {
        const status = getCourseStatus(course);
        if (typeFilter === "coming_soon") return status === "coming_soon";
        if (typeFilter === "free") return status !== "coming_soon" && (course.price === 0);
        if (typeFilter === "paid") return status !== "coming_soon" && (course.price > 0);
        return true;
      });
    }

    // Sorting
    switch (sortBy) {
      case "popular":
        filtered.sort((a, b) => b.students_count - a.students_count);
        break;
      case "rating":
        filtered.sort((a, b) => b.rating - a.rating);
        break;
      case "price-low":
        filtered.sort((a, b) => a.price - b.price);
        break;
      case "price-high":
        filtered.sort((a, b) => b.price - a.price);
        break;
      case "newest":
        break;
    }

    // Calculate discounted prices for courses with promotions
    const processedCourses = filtered.map(course => {
      if (course.discount_percentage && course.discount_percentage > 0) {
        const discountedPrice = course.price * (1 - course.discount_percentage / 100);
        return {
          ...course,
          discounted_price: discountedPrice,
          activePromotion: {
            name: 'Active Promotion',
            discount_percentage: course.discount_percentage,
            end_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
          }
        };
      }
      return course;
    });

    setFilteredCourses(processedCourses);
  };

  const levels = Array.from(new Set(courses.map((c) => c.level)));

  if (isLoading) {
    return (
      <div className="min-h-screen-dvh bg-background">
        <Navigation />
        <main className="container mx-auto page-container">
          <div className="mb-12 mt-20 text-center">
            <div className="h-12 w-96 bg-muted animate-pulse rounded mx-auto mb-4" />
            <div className="h-6 w-64 bg-muted animate-pulse rounded mx-auto" />
          </div>
          <div className="mb-8 space-y-4">
            <div className="h-12 max-w-2xl bg-muted animate-pulse rounded mx-auto" />
          </div>
          <SkeletonCourseGrid count={9} />
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen-dvh bg-background">
      <Navigation />

      <main className="container mx-auto page-container">
        {/* Header */}
        <div className="mb-10 md:mb-14 mt-16 md:mt-20 text-center">
          <div className="eyebrow mx-auto mb-5">
            <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
            <span>{t("courses.title")}</span>
          </div>
          <h1 className="heading-page mb-4">
            <span className="text-foreground">Conhecimento que </span>
            <span className="italic font-serif text-gold-foil">transforma</span>
          </h1>
          <div className="divider-foil mx-auto mb-5" />
          <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed px-2">
            {t("courses.description")}
          </p>
        </div>

        {/* Filters */}
        <div className="mb-8 space-y-4">
          {/* Search Bar */}
          <div className="relative max-w-2xl mx-auto">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground pointer-events-none" />
            <Input
              type="text"
              placeholder={t("courses.searchPlaceholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-12 bg-card border-border"
            />
          </div>

          {/* Filter Controls — stack 2 cols on mobile, inline on sm+ */}
          <div className="grid grid-cols-2 gap-3 sm:flex sm:flex-wrap sm:gap-4 sm:items-center sm:justify-center max-w-3xl mx-auto">
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-full sm:w-[200px] bg-card min-h-[44px]">
                <SelectValue placeholder={t("courses.allCategories")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("courses.allCategories")}</SelectItem>
                {categories.map((category) => (
                  <SelectItem key={category} value={category}>
                    {category}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={levelFilter} onValueChange={setLevelFilter}>
              <SelectTrigger className="w-full sm:w-[180px] bg-card min-h-[44px]">
                <SelectValue placeholder={t("courses.allLevels")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("courses.allLevels")}</SelectItem>
                {levels.map((level) => (
                  <SelectItem key={level} value={level}>
                    {level}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-full sm:w-[160px] bg-card min-h-[44px]">
                <SelectValue placeholder={t("courses.allTypes")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("courses.allTypes")}</SelectItem>
                <SelectItem value="free">{t("courses.free")}</SelectItem>
                <SelectItem value="paid">{t("courses.paid")}</SelectItem>
                <SelectItem value="coming_soon">{t("courses.comingSoon")}</SelectItem>
              </SelectContent>
            </Select>

            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-full sm:w-[200px] bg-card min-h-[44px]">
                <SelectValue placeholder={t("courses.sortBy")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="popular">{t("courses.mostPopular")}</SelectItem>
                <SelectItem value="rating">{t("courses.highestRated")}</SelectItem>
                <SelectItem value="price-low">{t("courses.priceLowToHigh")}</SelectItem>
                <SelectItem value="price-high">{t("courses.priceHighToLow")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-6 text-center text-muted-foreground">
          {t("courses.showing")} {filteredCourses.length} {filteredCourses.length === 1 ? t("courses.course") : t("courses.courses")}
        </div>

        {/* Course Grid */}
        {filteredCourses.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-xl text-muted-foreground">{t("courses.noCourses")}</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredCourses.map((course) => {
              const status = getCourseStatus(course);
              const isComingSoon = status === "coming_soon";
              return (
              <Card
                key={course.id}
                role={isComingSoon ? undefined : "button"}
                tabIndex={isComingSoon ? -1 : 0}
                onClick={() => {
                  if (!isComingSoon) openCoursePanel(course.id);
                }}
                onKeyDown={(e) => {
                  if (isComingSoon) return;
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    openCoursePanel(course.id);
                  }
                }}
                className={cn(
                  "card-interactive overflow-hidden group border-primary/15 hover:border-primary/40 transition-all duration-500",
                  !isComingSoon && "cursor-pointer",
                  selectedCourseId === course.id && "ring-2 ring-primary border-primary/60",
                )}
              >
                {/* Course Thumbnail */}
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={course.thumbnail_url} 
                    alt={course.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/0 via-transparent to-secondary/0 group-hover:from-primary/10 group-hover:to-secondary/10 transition-all duration-500" />
                  <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between flex-wrap gap-2">
                    <Badge variant="secondary" className="backdrop-blur-md border-primary/20">{course.category}</Badge>
                    <Badge variant="outline" className="backdrop-blur-md border-primary/30">{course.level}</Badge>
                    {isComingSoon && (
                      <Badge variant="outline" className="backdrop-blur-md bg-primary/10 text-primary border-primary/30">
                        {t("courses.comingSoon")}
                      </Badge>
                    )}
                    {course.activePromotion && !isComingSoon && (
                      <Badge variant="default" className="backdrop-blur-md gap-1 bg-gradient-to-r from-primary to-secondary text-primary-foreground border-0">
                        <Tag className="w-3 h-3" />
                        {course.activePromotion.discount_percentage}% OFF
                      </Badge>
                    )}
                  </div>
                </div>

                <CardHeader>
                  <CardTitle className="text-xl md:text-2xl font-serif italic group-hover:text-primary transition-colors leading-snug pb-1">
                    {course.title}
                  </CardTitle>
                  <CardDescription className="line-clamp-2">{course.description}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-6 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      <span>{course.duration}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      <span>{course.students_count}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Star className="w-4 h-4 fill-primary text-primary" />
                      <span>{course.rating}</span>
                    </div>
                  </div>
                  <div className="pt-2 border-t border-border">
                    <p className="text-sm text-muted-foreground">
                      {t("courses.instructor")}: {course.instructor_name}
                    </p>
                  </div>
                </CardContent>
                <CardFooter className="flex items-center justify-between">
                  <div>
                    {isComingSoon ? (
                      <span className="text-lg font-semibold text-muted-foreground">{t("courses.launchingSoon")}</span>
                    ) : course.discounted_price ? (
                      <>
                        <div className="flex items-center gap-2">
                          <span className="text-2xl font-bold text-gold-foil">
                            ${course.discounted_price.toFixed(2)}
                          </span>
                          <span className="text-lg text-muted-foreground line-through">
                            ${course.price}
                          </span>
                        </div>
                        <p className="text-sm text-primary font-medium mt-1">
                          {t("courses.save")} ${(course.price - course.discounted_price).toFixed(2)}!
                        </p>
                      </>
                    ) : course.price === 0 ? (
                      <span className="text-2xl font-bold text-primary">{t("courses.free", { defaultValue: "Free" })}</span>
                    ) : (
                      <span className="text-2xl font-bold text-gold-foil">${course.price}</span>
                    )}
                  </div>
                  {(() => {
                    const enrolled = enrollmentsMap.get(course.id);
                    const label = isComingSoon
                      ? t("courses.comingSoon")
                      : enrolled
                        ? (t("myCourses.cta.continue", { defaultValue: "Continuar" }) as string)
                        : t("courses.viewDetails");
                    return (
                      <Button
                        variant={isComingSoon ? "outline" : "default"}
                        disabled={isComingSoon}
                        className="group-hover:shadow-[var(--glow-primary)]"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isComingSoon) return;
                          openCoursePanel(course.id);
                        }}
                      >
                        {label}
                      </Button>
                    );
                  })()}
                </CardFooter>
              </Card>
              );
            })}
          </div>
        )}
      </main>
      <Footer />

      <CourseDetailsPanel
        courseId={selectedCourseId}
        enrollment={selectedCourseId ? enrollmentsMap.get(selectedCourseId) : null}
        onClose={closeCoursePanel}
      />
    </div>
  );
}
